import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useBackfillWearable,
  useConnectWearable,
  useDisconnectWearable,
  useReestimatePain,
  useSyncWearable,
  useWearableProviders,
  useWearableStatus,
  type WearableIntegration,
  type WearableProviderInfo,
} from '@pelvia/shared/hooks/patient/useWearables'
import {
  useAkuteStatus,
  useAkuteEnroll,
  missingDemographicsFrom,
} from '@pelvia/shared/hooks/patient/useAkute'
import WearableLogo from '@/components/WearableLogo'
import DemographicsModal from '@/components/DemographicsModal'
import SendNoteModal from '@/components/SendNoteModal'
import PelviaCareCheckout from '@/pages/patient/PelviaCareCheckout'
import { useDownloadConsultNote } from '@/hooks/useDownloadConsultNote'

function statusLabel(status: string | undefined): { text: string; tone: 'ok' | 'warn' | 'err' | 'neutral' } {
  switch (status) {
    case 'active':
      return { text: 'Connected', tone: 'ok' }
    case 'syncing':
      return { text: 'Syncing…', tone: 'warn' }
    case 'pending':
      return { text: 'Awaiting authorization', tone: 'warn' }
    case 'needs_reauth':
    case 'error':
      return { text: 'Needs reconnect', tone: 'err' }
    case 'revoked':
      return { text: 'Disconnected', tone: 'neutral' }
    default:
      return { text: 'Not connected', tone: 'neutral' }
  }
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso)
  const diffMin = Math.round((Date.now() - then.getTime()) / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const h = Math.round(diffMin / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  return `${d}d ago`
}

export default function IntegrationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const providers = useWearableProviders()
  const status = useWearableStatus()
  const connect = useConnectWearable()
  const disconnect = useDisconnectWearable()
  const sync = useSyncWearable()
  const backfill = useBackfillWearable()
  const reestimate = useReestimatePain()

  const akuteStatus = useAkuteStatus()
  const akuteEnroll = useAkuteEnroll()
  const { download: downloadNote, downloading } = useDownloadConsultNote()

  const [toast, setToast] = useState<string | null>(null)
  const [backfillFor, setBackfillFor] = useState<string | null>(null)
  const [disconnectFor, setDisconnectFor] = useState<string | null>(null)
  const [demographicsOpen, setDemographicsOpen] = useState(false)
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  // The send/preview modal: 'send' shows the picker, 'preview' is read-only.
  const [noteModalMode, setNoteModalMode] = useState<'send' | 'preview' | null>(null)
  // The clinician chosen in the picker, kept so we can retry the send after the
  // demographics modal without re-picking.
  const [pendingClinician, setPendingClinician] = useState<string | null>(null)

  // The Akute send only runs post-payment now, so any non-initial status means
  // the patient has already paid.
  const akuteState = akuteStatus.data?.status
  const hasEnrolled = ['synced', 'note_pushed', 'needs_resubmit', 'signed_off', 'error'].includes(
    akuteState || '',
  )
  // A pushed/signed note can't be re-sent (enroll no-ops on the existing note);
  // those states get preview / download instead of a resend that does nothing.
  const canResend = hasEnrolled && akuteState !== 'note_pushed' && akuteState !== 'signed_off'

  function runAkuteEnroll(clinicianId: string) {
    setPendingClinician(clinicianId)
    akuteEnroll.mutate(clinicianId, {
      onSuccess: (res) => {
        setNoteModalMode(null)
        setDemographicsOpen(false)
        setToast(
          res.status === 'signed_off'
            ? 'Your records are with your care team and your note is signed.'
            : 'Sent to your care team — a clinician will review and sign your note.',
        )
      },
      onError: (err) => {
        const missing = missingDemographicsFrom(err)
        if (missing) {
          setMissingFields(missing)
          setNoteModalMode(null)
          setDemographicsOpen(true)
        } else {
          setToast("Couldn't send your records right now. Please try again.")
        }
      },
    })
  }

  useEffect(() => {
    const connected = searchParams.get('connected')
    const err = searchParams.get('error')
    const fields = Number(searchParams.get('fields') || '0')
    const days = Number(searchParams.get('days') || '0')
    if (err) {
      setToast(`Connection failed: ${err.replace(/_/g, ' ')}`)
    } else if (connected) {
      const fieldsLine = fields > 0 ? ` · pre-filled ${fields} profile field${fields === 1 ? '' : 's'}` : ''
      const daysLine = days > 0 ? ` · estimating ${days} days of pain` : ''
      setToast(`Connected to ${connected.toUpperCase()}${fieldsLine}${daysLine}`)
    }
    if (connected || err) {
      const copy = new URLSearchParams(searchParams)
      copy.delete('connected'); copy.delete('error'); copy.delete('fields'); copy.delete('days')
      setSearchParams(copy, { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const integrationsByProvider = useMemo(() => {
    const map: Record<string, WearableIntegration> = {}
    for (const i of status.data?.integrations ?? []) map[i.provider] = i
    return map
  }, [status.data])

  const providerList: WearableProviderInfo[] = providers.data?.providers ?? []

  return (
    <div className="patient-page integrations-page">
      <header className="patient-page-header">
        <h1>Wearables</h1>
        <p className="patient-page-sub">
          Connect a device to auto-fill your daily check-in, pre-fill your profile, and estimate pain for days you didn't log.
        </p>
      </header>

      {toast && (
        <div className="integrations-toast" role="status">
          {toast}
          <button type="button" onClick={() => setToast(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {akuteStatus.data?.enabled && (
        <section className="care-team-card">
          <h2>Your care team</h2>
          <p>
            A board-certified clinician reviews your Pelvia history, signs a consult
            note for your insurance, and returns your next step (OB/GYN eval or
            surgery consult) within 72 hours.
          </p>
          <AkuteStatusLine status={akuteState} />
          <div className="integration-actions">
            {akuteState === 'signed_off' ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => downloadNote('pdf')}
                disabled={downloading}
              >
                {downloading ? 'Preparing…' : 'Download your consult note'}
              </button>
            ) : akuteState === 'note_pushed' ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setNoteModalMode('preview')}
              >
                Preview my note
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={canResend ? () => setNoteModalMode('send') : () => setCheckoutOpen(true)}
                disabled={akuteEnroll.isPending}
              >
                {akuteEnroll.isPending
                  ? 'Sending…'
                  : akuteState === 'needs_resubmit'
                    ? 'Resubmit my note'
                    : canResend
                      ? 'Resend my records'
                      : 'Get my note signed — $300'}
              </button>
            )}
          </div>
        </section>
      )}

      <section className="integrations-grid">
        {providerList.map((p) => {
          const it = integrationsByProvider[p.name]
          const sl = statusLabel(it?.status)
          const isSupported = p.status === 'supported'
          const isConnected = it?.status === 'active' || it?.status === 'syncing'
          return (
            <article key={p.name} className={`integration-card ${isSupported ? '' : 'is-disabled'}`}>
              <header className="integration-card-head">
                <h2 aria-label={p.display}>
                  <WearableLogo provider={p.name} fallbackName={p.display} height={22} />
                </h2>
                <span className={`integration-status integration-status-${sl.tone}`}>
                  {isSupported ? sl.text : 'Coming soon'}
                </span>
              </header>

              {!isSupported && (
                <p className="integration-note">We'll let you know when {p.display} is ready.</p>
              )}

              {isSupported && (
                <>
                  <dl className="integration-meta">
                    <div>
                      <dt>Last sync</dt>
                      <dd>{formatRelative(it?.last_sync_at ?? null)}</dd>
                    </div>
                    <div>
                      <dt>Connected</dt>
                      <dd>{formatRelative(it?.connected_at ?? null)}</dd>
                    </div>
                  </dl>

                  {it?.last_error && (
                    <p className="integration-error">{it.last_error}</p>
                  )}

                  <div className="integration-actions">
                    {!isConnected && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => connect.mutate(p.name)}
                        disabled={connect.isPending}
                      >
                        {connect.isPending ? 'Connecting…' : `Connect ${p.display}`}
                      </button>
                    )}
                    {isConnected && (
                      <>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => sync.mutate(p.name)}
                          disabled={sync.isPending}
                        >
                          {sync.isPending ? 'Syncing…' : 'Sync now'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setBackfillFor(p.name)}
                          disabled={backfill.isPending}
                        >
                          {backfill.isPending ? 'Queuing…' : 'Estimate past pain'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => setDisconnectFor(p.name)}
                          disabled={disconnect.isPending}
                        >
                          Disconnect
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </article>
          )
        })}
      </section>

      {backfillFor && (
        <BackfillConsentModal
          providerName={backfillFor}
          displayName={providerList.find((p) => p.name === backfillFor)?.display || backfillFor}
          onClose={() => setBackfillFor(null)}
          onConfirm={(days) => {
            backfill.mutate({ provider: backfillFor, days })
            reestimate.mutate({ provider: backfillFor, days })
            setBackfillFor(null)
            setToast(`Estimating pain for the last ${days} days — this runs in the background.`)
          }}
        />
      )}

      {disconnectFor && (
        <DisconnectConfirmModal
          displayName={providerList.find((p) => p.name === disconnectFor)?.display || disconnectFor}
          pending={disconnect.isPending}
          onClose={() => setDisconnectFor(null)}
          onConfirm={() => {
            disconnect.mutate(disconnectFor, {
              onSuccess: () => {
                setDisconnectFor(null)
                setToast(`Disconnected. Your history is preserved.`)
              },
            })
          }}
        />
      )}

      {noteModalMode && (
        <SendNoteModal
          mode={noteModalMode}
          selectedId={akuteStatus.data?.clinician_user_id}
          sending={akuteEnroll.isPending}
          onClose={() => setNoteModalMode(null)}
          onConfirm={runAkuteEnroll}
        />
      )}

      {demographicsOpen && (
        <DemographicsModal
          missingFields={missingFields}
          onClose={() => setDemographicsOpen(false)}
          onSaved={() => pendingClinician && runAkuteEnroll(pendingClinician)}
        />
      )}

      {checkoutOpen && (
        <PelviaCareCheckout onClose={() => setCheckoutOpen(false)} />
      )}
    </div>
  )
}

function AkuteStatusLine({ status }: { status?: string }) {
  const label: Record<string, string> = {
    synced: 'Your record is with your care team.',
    note_pushed: 'Your consult note is awaiting clinician sign-off.',
    needs_resubmit: 'Your clinician asked you to resubmit with your latest information.',
    signed_off: 'Your consult note has been signed.',
    error: "The last send didn't complete — try again.",
  }
  if (!status || status === 'not_enrolled' || status === 'pending') return null
  const tone = status === 'signed_off' ? 'ok' : status === 'error' ? 'err' : 'warn'
  return <p className={`integration-status integration-status-${tone}`}>{label[status]}</p>
}

function DisconnectConfirmModal({
  displayName,
  pending,
  onClose,
  onConfirm,
}: {
  displayName: string
  pending: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <h3>Disconnect {displayName}?</h3>
        <p>
          We'll stop pulling new data from {displayName} and revoke the connection.
          Your biometric history and any pain estimates we already computed stay
          in your account — you can reconnect anytime to resume auto-tracking.
        </p>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? 'Disconnecting…' : `Disconnect ${displayName}`}
          </button>
        </div>
      </div>
    </div>
  )
}

function BackfillConsentModal({
  providerName,
  displayName,
  onClose,
  onConfirm,
}: {
  providerName: string
  displayName: string
  onClose: () => void
  onConfirm: (days: number) => void
}) {
  const [days, setDays] = useState(180)
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <h3>Estimate past pain scores</h3>
        <p>
          Pelvia will read the last <strong>{days}</strong> days of {displayName} biometrics
          (HRV, resting heart rate, sleep, skin temperature) to estimate pelvic pain scores for
          days you didn't log.
        </p>
        <p>
          Estimates are clearly labeled and never replace entries you've already made. You can
          delete them anytime from the calendar.
        </p>
        <div className="backfill-days">
          {[30, 90, 180].map((d) => (
            <button
              key={d}
              type="button"
              className={`chip${days === d ? ' selected' : ''}`}
              onClick={() => setDays(d)}
            >
              Last {d} days
            </button>
          ))}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onConfirm(days)}
          >
            Estimate last {days} days
          </button>
        </div>
        <p className="modal-footnote" data-provider={providerName}>
          You can disconnect and delete estimates at any time.
        </p>
      </div>
    </div>
  )
}
